/**
 * GENERATED templates: the plan's "generate" level. When no template plays
 * like an idea, a model composes a whole game from library modules — the
 * spec plus the contract every template carries (disclosure, soundtrack
 * direction, proof obligations, verb proofs) — and that game becomes a
 * template private to its creation. It is held to the catalog's own rules
 * here (shape, a valid deterministic spec, one holding verb proof per core
 * verb, obligations its modules can meet) and then admitted by the same
 * solver proof as any template.
 *
 * A generated template has no tunables (its game is authored, not tuned)
 * and the standard text slots, so the rest of Create (titles, role lines,
 * the background line) patches it like any template.
 */
import type { AnyComposeModule } from '../module.js';
import { validateGameSpec, type GameSpecV1 } from '../spec.js';
import { compileGameSpec } from '../compile.js';
import { specModuleIds } from './structure.js';
import { verbProofFailures } from './verbProofs.js';
import { VERB_PROOF_MS, verbLeadProblem, type TemplateDisclosureV1, type TemplateV1, type VerbInputV1, type VerbProofV1 } from './template.js';

export const GENERATED_TEMPLATE_PREFIX = 'generated/';
export const GENERATED_TEMPLATE_FAMILY = 'generated';
const GENERATED_ID = /^generated\/[a-z][a-z0-9-]{0,47}$/;

/** The text slots of every generated template (the catalog's standard set). */
export const GENERATED_TEMPLATE_CONTENT_SLOTS: TemplateV1['contentSlots'] = Object.freeze([
  { path: '/title', maxLength: 80 },
  { path: '/summary', maxLength: 160 },
  { path: '/archetypes/*/role', maxLength: 120 },
  { path: '/presentation/backgroundRole', maxLength: 120 },
]) as TemplateV1['contentSlots'];

export const GENERATED_TEMPLATE_LIMITS = Object.freeze({
  title: 80, pitch: 300, coreVerbs: 4, verb: 24, mechanics: 8, line: 240, events: 8, signature: 8,
  mood: 80, genre: 60, instrument: 40, instrumentation: 6, tempoMin: 50, tempoMax: 200,
});

/** What a model writes for a generated template; the platform adds the id,
 * family, (empty) tunables and the standard content slots. */
export type GeneratedTemplateDraftV1 = Pick<TemplateV1,
  'title' | 'pitch' | 'disclosure' | 'music' | 'spec' | 'requirements' | 'signatureModules' | 'verbProofs'>;

const DRAFT_KEYS = ['title', 'pitch', 'disclosure', 'music', 'spec', 'requirements', 'signatureModules', 'verbProofs'];
const TEMPLATE_KEYS = ['id', 'family', 'tunables', 'contentSlots', ...DRAFT_KEYS];
const VERB_INPUTS: readonly (VerbInputV1 | 'tap')[] = ['left', 'right', 'up', 'down', 'a', 'b', 'tap'];

function plain(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value) && [Object.prototype, null].includes(Object.getPrototypeOf(value));
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[], at: string, errors: string[]): void {
  const extra = Object.keys(value).filter(k => !keys.includes(k));
  const missing = keys.filter(k => !Object.hasOwn(value, k));
  if (extra.length) errors.push(`${at}: unsupported fields ${extra.join(', ')}`);
  if (missing.length) errors.push(`${at}: missing ${missing.join(', ')}`);
}

const text = (v: unknown, max: number) => typeof v === 'string' && v.trim() === v && v.length >= 1 && v.length <= max;
const lines = (v: unknown, min: number, max: number, each: number) =>
  Array.isArray(v) && v.length >= min && v.length <= max && v.every(s => text(s, each));

/** The template a draft becomes under `id`. */
export function generatedTemplateV1(id: string, draft: GeneratedTemplateDraftV1): TemplateV1 {
  return {
    id, family: GENERATED_TEMPLATE_FAMILY, title: draft.title, pitch: draft.pitch, disclosure: draft.disclosure, music: draft.music,
    spec: draft.spec, tunables: [], contentSlots: [...GENERATED_TEMPLATE_CONTENT_SLOTS], requirements: draft.requirements,
    signatureModules: draft.signatureModules, verbProofs: draft.verbProofs,
  };
}

/** Validate an UNTRUSTED generated template (a whole {@link TemplateV1}, as
 * carried by an accepted spec) against a module registry; every error is
 * collected. Short of the solver proof, which admission runs. */
export function validateGeneratedTemplate(input: unknown, modules: ReadonlyMap<string, AnyComposeModule>):
  { ok: true; template: TemplateV1 } | { ok: false; errors: string[] } {
  return checkGeneratedTemplate(input, modules, true);
}

/** Parse a base template's complete structure without executing its intended
 * final verbs on that unintegrated base. This is not gameplay admission. */
export function validateGeneratedTemplateStructure(input: unknown, modules: ReadonlyMap<string, AnyComposeModule>):
  { ok: true; template: TemplateV1 } | { ok: false; errors: string[] } {
  return checkGeneratedTemplate(input, modules, false);
}

function checkGeneratedTemplate(input: unknown, modules: ReadonlyMap<string, AnyComposeModule>, executeVerbs: boolean):
  { ok: true; template: TemplateV1 } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  if (!plain(input)) return { ok: false, errors: ['template: expected an object'] };
  exactKeys(input, TEMPLATE_KEYS, 'template', errors);
  if (typeof input.id !== 'string' || !GENERATED_ID.test(input.id)) errors.push(`template.id: expected ${GENERATED_TEMPLATE_PREFIX}<kebab-case name>`);
  if (input.family !== GENERATED_TEMPLATE_FAMILY) errors.push(`template.family: expected "${GENERATED_TEMPLATE_FAMILY}"`);
  if (!Array.isArray(input.tunables) || input.tunables.length) errors.push('template.tunables: a generated template has none');
  // Compared by value, never by key order: a stored template comes back from
  // JSONB (or canonical JSON) with its keys reordered.
  const slots = input.contentSlots;
  if (!Array.isArray(slots) || slots.length !== GENERATED_TEMPLATE_CONTENT_SLOTS.length || !slots.every((slot, i) => plain(slot)
    && Object.keys(slot).length === 2 && slot.path === GENERATED_TEMPLATE_CONTENT_SLOTS[i]!.path && slot.maxLength === GENERATED_TEMPLATE_CONTENT_SLOTS[i]!.maxLength)) {
    errors.push('template.contentSlots: expected the standard text slots');
  }
  const draft = validateGeneratedTemplateDraftErrors(Object.fromEntries(DRAFT_KEYS.filter(k => Object.hasOwn(input, k)).map(k => [k, input[k]])), modules, executeVerbs);
  errors.push(...draft.errors);
  if (errors.length || !draft.draft) return { ok: false, errors };
  return { ok: true, template: generatedTemplateV1(input.id as string, draft.draft) };
}

/** Validate an UNTRUSTED draft (a model's reply) against a module registry. */
export function validateGeneratedTemplateDraft(input: unknown, modules: ReadonlyMap<string, AnyComposeModule>):
  { ok: true; draft: GeneratedTemplateDraftV1 } | { ok: false; errors: string[] } {
  return checkGeneratedTemplateDraft(input, modules, true);
}

/** Checked-scaffold parsing: retain every intended verb and its exact proof
 * shape; execution awaits integration and the final derived-game check. */
export function validateGeneratedTemplateDraftStructure(input: unknown, modules: ReadonlyMap<string, AnyComposeModule>):
  { ok: true; draft: GeneratedTemplateDraftV1 } | { ok: false; errors: string[] } {
  return checkGeneratedTemplateDraft(input, modules, false);
}

function checkGeneratedTemplateDraft(input: unknown, modules: ReadonlyMap<string, AnyComposeModule>, executeVerbs: boolean):
  { ok: true; draft: GeneratedTemplateDraftV1 } | { ok: false; errors: string[] } {
  if (!plain(input)) return { ok: false, errors: ['template: expected an object'] };
  const errors: string[] = [];
  exactKeys(input, DRAFT_KEYS, 'template', errors);
  const checked = validateGeneratedTemplateDraftErrors(input, modules, executeVerbs);
  errors.push(...checked.errors);
  return errors.length || !checked.draft ? { ok: false, errors } : { ok: true, draft: checked.draft };
}

function validateGeneratedTemplateDraftErrors(input: Record<string, unknown>, modules: ReadonlyMap<string, AnyComposeModule>, executeVerbs: boolean):
  { draft: GeneratedTemplateDraftV1 | null; errors: string[] } {
  const L = GENERATED_TEMPLATE_LIMITS;
  const errors: string[] = [];
  if (!text(input.title, L.title)) errors.push(`template.title: expected 1..${L.title} characters`);
  if (!text(input.pitch, L.pitch)) errors.push(`template.pitch: expected 1..${L.pitch} characters`);
  const d = input.disclosure;
  if (!plain(d)) errors.push('template.disclosure: expected { coreVerbs, mechanics, controls, winCondition }');
  else {
    exactKeys(d, ['coreVerbs', 'mechanics', 'controls', 'winCondition'], 'template.disclosure', errors);
    if (!lines(d.coreVerbs, 1, L.coreVerbs, L.verb) || new Set(d.coreVerbs as string[]).size !== (d.coreVerbs as string[]).length) {
      errors.push(`template.disclosure.coreVerbs: expected 1..${L.coreVerbs} distinct verbs of at most ${L.verb} characters`);
    }
    if (!lines(d.mechanics, 1, L.mechanics, L.line)) errors.push(`template.disclosure.mechanics: expected 1..${L.mechanics} lines of at most ${L.line} characters`);
    if (!text(d.controls, L.line)) errors.push(`template.disclosure.controls: expected 1..${L.line} characters`);
    if (!text(d.winCondition, L.line)) errors.push(`template.disclosure.winCondition: expected 1..${L.line} characters`);
  }
  const m = input.music;
  if (!plain(m)) errors.push('template.music: expected { mood, genre, tempoBpm, instrumentation }');
  else {
    exactKeys(m, ['mood', 'genre', 'tempoBpm', 'instrumentation'], 'template.music', errors);
    if (!text(m.mood, L.mood)) errors.push(`template.music.mood: expected 1..${L.mood} characters`);
    if (!text(m.genre, L.genre)) errors.push(`template.music.genre: expected 1..${L.genre} characters`);
    if (!Number.isInteger(m.tempoBpm) || (m.tempoBpm as number) < L.tempoMin || (m.tempoBpm as number) > L.tempoMax) {
      errors.push(`template.music.tempoBpm: expected a whole number ${L.tempoMin}..${L.tempoMax}`);
    }
    if (!lines(m.instrumentation, 1, L.instrumentation, L.instrument)) {
      errors.push(`template.music.instrumentation: expected 1..${L.instrumentation} instruments of at most ${L.instrument} characters`);
    }
  }
  const spec = validateGameSpec(input.spec, modules);
  if (!spec.ok) errors.push(...spec.errors.map(e => `template.spec: ${e}`));
  else if (spec.spec.title !== input.title) errors.push('template.spec.title: must equal the template title');
  const r = input.requirements;
  const used = spec.ok ? specModuleIds(spec.spec) : new Set<string>();
  const emittable = new Set([...used].flatMap(id => modules.get(id)?.events ?? []));
  if (!plain(r) || Object.keys(r).join() !== 'events' || !Array.isArray(r.events) || r.events.length > L.events
    || r.events.some(e => typeof e !== 'string')) {
    errors.push(`template.requirements: expected { events: [at most ${L.events} event kinds] }`);
  } else if (spec.ok) {
    for (const event of r.events as string[]) {
      const [kind, archetype] = event.split(':');
      if (!emittable.has(kind!)) errors.push(`template.requirements.events: no module of the game emits "${kind}"`);
      if (archetype !== undefined && !Object.hasOwn(spec.spec.archetypes, archetype)) errors.push(`template.requirements.events: "${event}" names no kind of the game`);
    }
  }
  const s = input.signatureModules;
  if (!Array.isArray(s) || s.length > L.signature || s.some(id => typeof id !== 'string')) {
    errors.push(`template.signatureModules: expected at most ${L.signature} module ids`);
  } else if (spec.ok) {
    for (const id of s as string[]) {
      // Name the valid entries: Switchback's composer listed the event "load" here three times.
      if (!used.has(id)) errors.push(`template.signatureModules: the game does not use ${id} (list module ids the game uses: ${[...used].sort().join(', ')})`);
    }
  }
  const verbs = plain(d) && Array.isArray(d.coreVerbs) ? d.coreVerbs as string[] : [];
  const v = input.verbProofs;
  if (!Array.isArray(v) || v.length !== verbs.length || v.some((p, i) => !plain(p) || p.verb !== verbs[i])) {
    errors.push(`template.verbProofs: expected one proof per core verb, in order (${verbs.join(', ')})`);
  } else {
    v.forEach((p: Record<string, unknown>, i) => {
      const at = `template.verbProofs[${i}]`;
      const e = p.expect;
      if (!VERB_INPUTS.includes(p.input as VerbInputV1) || !Number.isInteger(p.ms) || (p.ms as number) < VERB_PROOF_MS.min || (p.ms as number) > VERB_PROOF_MS.max
        || !plain(e) || !((e.kind === 'event' && typeof e.event === 'string') || (e.kind === 'state-change' && typeof e.path === 'string'))
        || (p.input === 'tap') !== (typeof p.target === 'string')) {
        errors.push(`${at}: expected { verb, input, ms ${VERB_PROOF_MS.min}..${VERB_PROOF_MS.max}, lead?, expect: { kind: "event", event } | { kind: "state-change", path } } (and target for "tap")`);
      }
      const lead = verbLeadProblem(p.lead, p.input === 'tap');
      if (lead) errors.push(`${at}: ${lead}`);
    });
    if (executeVerbs && !errors.length && spec.ok) {
      errors.push(...verbProofFailures(compileGameSpec(spec.spec, modules), v as VerbProofV1[]).map(f => `template.verbProofs: ${f}`));
    }
  }
  if (errors.length || !spec.ok) return { draft: null, errors };
  return { draft: structuredClone({
    title: input.title as string, pitch: input.pitch as string, disclosure: d as unknown as TemplateDisclosureV1,
    music: m as unknown as TemplateV1['music'], spec: spec.spec as GameSpecV1, requirements: { events: [...(r as { events: string[] }).events] },
    signatureModules: [...s as string[]], verbProofs: v as VerbProofV1[],
  }), errors };
}
